/**
 * crewWriteupsData.js — Supabase CRUD for the `crew_writeups` table.
 *
 * Backend table + admin-only RLS defined in /app/sql/009_crew_writeups.sql.
 * All operations are admin-only at the database level — the helpers below
 * surface errors as toast-friendly strings rather than throwing so the UI
 * can present "Admin access required" gracefully when a non-admin somehow
 * lands on the write-ups screen.
 */

import { supabase } from '@/lib/supabaseClient';

export const WRITEUP_CATEGORIES = [
  'positive',
  'negative',
  'verbal_warning',
  'written_warning',
];

const todayIso = () => new Date().toISOString().slice(0, 10);

/* ----------------------------------------------------------------------------
 * Read — list a single employee's write-ups (newest first).
 *
 * Selects the author's display name in one query via FK join to `profiles`.
 * The join is best-effort: if it returns null (profile deleted) we fall back
 * to a stable label.
 * ------------------------------------------------------------------------- */
export const listWriteups = async (crewId) => {
  if (!crewId) return [];
  const { data, error } = await supabase
    .from('crew_writeups')
    .select(
      'id,crew_id,date,category,note,created_at,created_by,profiles:created_by(full_name)'
    )
    .eq('crew_id', crewId)
    .order('date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map((row) => ({
    id: row.id,
    crew_id: row.crew_id,
    date: row.date,
    category: row.category,
    note: row.note || '',
    created_at: row.created_at,
    created_by: row.created_by,
    author_name:
      row?.profiles?.full_name ||
      (row.created_by ? row.created_by.slice(0, 8) : '—'),
  }));
};

/* ----------------------------------------------------------------------------
 * Create — add a new write-up. The DB stamps `created_by = auth.uid()` via
 * DEFAULT (and the RLS policy also validates `created_by = auth.uid()` so
 * a malicious client cannot spoof attribution).
 * ------------------------------------------------------------------------- */
export const addWriteup = async ({
  crewId,
  date,
  category,
  note,
} = {}) => {
  if (!crewId) throw new Error('crew_id required');
  if (!WRITEUP_CATEGORIES.includes(category)) {
    throw new Error('Invalid write-up category');
  }
  if (!note || !String(note).trim()) {
    throw new Error('Note cannot be empty');
  }
  // Stamp the current user's id explicitly (the RLS WITH CHECK requires
  // created_by = auth.uid(); the DEFAULT also handles this but being
  // explicit makes the intent obvious in the wire payload).
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id;
  const payload = {
    crew_id: String(crewId),
    date: date || todayIso(),
    category,
    note: String(note).trim(),
    ...(userId ? { created_by: userId } : {}),
  };
  const { data, error } = await supabase
    .from('crew_writeups')
    .insert(payload)
    .select(
      'id,crew_id,date,category,note,created_at,created_by,profiles:created_by(full_name)'
    )
    .single();
  if (error) throw error;
  return {
    ...data,
    author_name:
      data?.profiles?.full_name ||
      (data?.created_by ? data.created_by.slice(0, 8) : '—'),
  };
};

/* ----------------------------------------------------------------------------
 * Delete — remove a single write-up (admin only).
 * ------------------------------------------------------------------------- */
export const deleteWriteup = async (id) => {
  if (!id) throw new Error('id required');
  const { error } = await supabase.from('crew_writeups').delete().eq('id', id);
  if (error) throw error;
  return true;
};
